class MarqueeSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    this.toggleButton = this.querySelector('[data-marquee-toggle]');
    this.groups = Array.from(this.querySelectorAll('[data-marquee-group]'));

    if (!this.track || this.groups.length < 2) return;

    this.speedSeconds = parseFloat(this.dataset.speed) || 40;
    this.direction = this.dataset.direction === 'right' ? 1 : -1;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.offset = 0;
    this.groupWidth = 0;
    this.paused = false;
    this.hovered = false;
    this.dragging = false;
    this.lastTime = null;
    this.suppressNextClick = false;

    this.measure();
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.groups[0]);

    this.toggleButton?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.classList.toggle('marquee-slider--paused', this.paused);
      this.toggleButton.setAttribute('aria-pressed', String(this.paused));
      this.toggleButton.setAttribute('aria-label', this.paused ? 'Play slider' : 'Pause slider');
    });

    this.addEventListener('pointerenter', () => {
      this.hovered = true;
    });
    this.addEventListener('pointerleave', () => {
      this.hovered = false;
    });

    this.setupDrag();

    this.tick = this.tick.bind(this);
    this.frame = requestAnimationFrame(this.tick);
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
  }

  measure() {
    this.groupWidth = this.groups[0].getBoundingClientRect().width;
  }

  tick(time) {
    if (this.lastTime === null) this.lastTime = time;
    const delta = Math.min((time - this.lastTime) / 1000, 0.1);
    this.lastTime = time;

    if (!this.paused && !this.hovered && !this.dragging && !this.reducedMotion && this.groupWidth > 0) {
      const pxPerSecond = this.groupWidth / this.speedSeconds;
      this.offset += this.direction * pxPerSecond * delta;
    }

    // Three groups are rendered so there's a full group-width of drag buffer on either
    // side before a wrap is needed. Wrapping is skipped mid-drag so it can't fight the
    // pointer's own delta tracking - any needed wrap is deferred until the drag ends.
    if (!this.dragging && this.groupWidth > 0) {
      while (this.offset <= -this.groupWidth * 2) this.offset += this.groupWidth;
      while (this.offset > 0) this.offset -= this.groupWidth;
    }

    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
    this.frame = requestAnimationFrame(this.tick);
  }

  setupDrag() {
    let startX = 0;
    let startOffset = 0;
    let totalMovement = 0;
    let pointerId = null;

    const onPointerDown = (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      pointerId = event.pointerId;
      this.dragging = true;
      startX = event.clientX;
      startOffset = this.offset;
      totalMovement = 0;
      this.classList.add('marquee-slider--dragging');
      this.track.setPointerCapture?.(pointerId);
    };

    const onPointerMove = (event) => {
      if (!this.dragging || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      totalMovement = Math.max(totalMovement, Math.abs(dx));
      this.offset = startOffset + dx;
    };

    const endDrag = (event) => {
      if (!this.dragging || (pointerId !== null && event.pointerId !== pointerId)) return;
      this.dragging = false;
      pointerId = null;
      this.classList.remove('marquee-slider--dragging');

      // A drag that moved more than a few pixels shouldn't also fire the link it started
      // on - the next click on the track is swallowed once, then the flag resets.
      if (totalMovement > 6) {
        this.suppressNextClick = true;
      }
    };

    this.track.addEventListener('pointerdown', onPointerDown);
    this.track.addEventListener('pointermove', onPointerMove);
    this.track.addEventListener('pointerup', endDrag);
    this.track.addEventListener('pointercancel', endDrag);

    this.track.addEventListener(
      'click',
      (event) => {
        if (this.suppressNextClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressNextClick = false;
        }
      },
      true
    );
  }
}

customElements.define('marquee-slider', MarqueeSlider);
